import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

/**
 * Storage endpoints for the admin usage + storage settings pages. All three
 * live under /api/admin/storage/* — see worker/routes/admin.js. ADMIN-only
 * (backend rejects non-ADMIN with 403).
 */

export type StorageCurrent = {
  activeBytes: number;
  trashBytes: number;
  imageBytes: number;   // project cover images
  logoBytes: number;    // workspace brand logo
  totalBytes: number;
};

export type StoragePeak = {
  /** Peak total bytes seen in the rolling 30-day window ending today. */
  peakLast30dBytes: number;
  /** YYYY-MM-DD of the peak day, or null if no snapshots yet. */
  peakDate: string | null;
  /** Alias for peakLast30dBytes — this is the number billing sees. */
  billableBytes: number;
  windowStart: string;
  windowEnd: string;
};

export type StorageHistoryRow = {
  date: string;
  activeBytes: number;
  trashBytes: number;
  totalBytes: number;
};

/** GET /api/admin/storage/current — live active/trash/total for this workspace. */
export function useStorageCurrent(enabled = true) {
  return useQuery<StorageCurrent>({
    queryKey: ["admin", "storage", "current"],
    queryFn: () => api.get<StorageCurrent>("/admin/storage/current"),
    enabled,
    staleTime: 30_000,
  });
}

/** GET /api/admin/storage/peak — billing metric (peakLast30dBytes). */
export function useStoragePeak(enabled = true) {
  return useQuery<StoragePeak>({
    queryKey: ["admin", "storage", "peak"],
    queryFn: () => api.get<StoragePeak>("/admin/storage/peak"),
    enabled,
    staleTime: 60_000,
  });
}

/** GET /api/admin/storage/history — 30 daily rows for a sparkline. */
export function useStorageHistory(enabled = true) {
  return useQuery<StorageHistoryRow[]>({
    queryKey: ["admin", "storage", "history"],
    queryFn: () => api.get<StorageHistoryRow[]>("/admin/storage/history"),
    enabled,
    staleTime: 60_000,
  });
}
